import React from 'react';
import {useNavigate} from 'react-router';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';

interface MileageIntroProps {
  vin: string;
}

function MileageIntro({vin}: MileageIntroProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T40049')}</h1>
      </Header>

      <main className="content page_eco_mileage">
        <section className="section section_eco_intro">
          <div
            className="section_heading type_main"
            style={{whiteSpace: 'pre-wrap'}}
          >
            <h2 className="section_heading_title">{t('T40050')}</h2>
            <p className="section_heading_desc">{t('T40051')}</p>
          </div>
          <div className="section_visual visual_eco_mileage">
            <ImageSelector
              className="section_visual_img"
              alt="section_visual_img"
              kiaLightSrc={() => import('@di-shared/assets/img/visual/eco_mileage.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/visual/eco_mileage.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/visual/hyundai/eco_mileage.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/hyundai/eco_mileage.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/visual/genesis/eco_mileage.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/visual/genesis/eco_mileage.png')}
            />
          </div>
          <div className="section_container">
            <div className="section_container_header">
              <h3
                className="title_xxsmall"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40052')}
              </h3>
            </div>

            <ul className="eco_intro_list">
              <li className="eco_intro_item">
                <div className="eco_intro_icon">
                  <span className="ic ic_work_end_24px"></span>
                </div>
                <div className="eco_intro_info">
                  <strong className="eco_intro_title">{t('T40053')}</strong>
                  <span className="eco_intro_desc">{t('T40054')}</span>
                </div>
              </li>
              <li className="eco_intro_item">
                <div className="eco_intro_icon">
                  <span className="ic ic_like_24px"></span>
                </div>
                <div className="eco_intro_info">
                  <strong className="eco_intro_title">{t('T40055')}</strong>
                  <span className="eco_intro_desc">{t('T40056')}</span>
                </div>
              </li>
            </ul>
          </div>
          <div className="section_container">
            <div className="section_container_header">
              <h3
                className="title_xxsmall"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40059')}
              </h3>
            </div>

            <ul className="eco_intro_list">
              <li className="eco_intro_item">
                <div className="eco_intro_icon">
                  <span className="ic ic_memo_24px"></span>
                </div>
                <div className="eco_intro_info">
                  <strong className="eco_intro_title">{t('T40060')}</strong>
                  <span className="eco_intro_desc">{t('T40061')}</span>
                </div>
              </li>
              <li className="eco_intro_item">
                <div className="eco_intro_icon">
                  <span
                    className="ic ic_drive_distance_24px"
                    aria-hidden="true"
                  ></span>
                </div>
                <div className="eco_intro_info">
                  <strong className="eco_intro_title">{t('T40062')}</strong>
                  <span className="eco_intro_desc">{t('T40063')}</span>
                </div>
              </li>
              <li className="eco_intro_item">
                <div className="eco_intro_icon">
                  <span className="ic ic_heart_24px"></span>
                </div>
                <div className="eco_intro_info">
                  <strong className="eco_intro_title">{t('T40064')}</strong>
                  <span className="eco_intro_desc">{t('T40065')}</span>
                </div>
              </li>
            </ul>
          </div>
          <div className="section_container">
            <div className="section_container_header">
              <h3
                className="title_xsmall_b"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40066')}
              </h3>
            </div>

            <div className="container_instruction_box">
              <Swiper
                slidesPerView="auto"
                modules={[Pagination]}
                className="container-instruction-swiper"
                observer
                observeParents
                pagination={{el: '.swiper-pagination', type: 'bullets'}}
              >
                <SwiperSlide>
                  <div className="container_instruction_imgbox">
                    <ImageSelector
                      className="container_instruction_img"
                      alt="container_instruction_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step1_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step1.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step1_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step1.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step1_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step1.png')}
                    />
                  </div>
                  <div className="container_instruction_content">
                    <strong className="container_instruction_title">{t('T40129_$n', {n: 1})}</strong>
                    <p
                      className="container_instruction_desc"
                      style={{whiteSpace: 'pre-wrap'}}
                    >
                      {t('T40067')}
                    </p>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="container_instruction_imgbox">
                    <ImageSelector
                      className="container_instruction_img"
                      alt="container_instruction_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step2_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step2.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step2_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step2.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step2_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step2.png')}
                    />
                  </div>
                  <div className="container_instruction_content">
                    <strong className="container_instruction_title">{t('T40129_$n', {n: 2})}</strong>
                    <p
                      className="container_instruction_desc"
                      style={{whiteSpace: 'pre-wrap'}}
                    >
                      {t('T40068')}
                    </p>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="container_instruction_imgbox">
                    <ImageSelector
                      className="container_instruction_img"
                      alt="container_instruction_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step3_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step3.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step3_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step3.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step3_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step3.png')}
                    />
                  </div>
                  <div className="container_instruction_content">
                    <strong className="container_instruction_title">{t('T40129_$n', {n: 3})}</strong>
                    <p
                      className="container_instruction_desc"
                      style={{whiteSpace: 'pre-wrap'}}
                    >
                      {t('T40069')}
                    </p>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="container_instruction_imgbox">
                    <ImageSelector
                      className="container_instruction_img"
                      alt="container_instruction_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step4_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/eco_mileage/instruction_step4.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step4_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/eco_mileage/hyundai/instruction_step4.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step4_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/eco_mileage/genesis/instruction_step4.png')}
                    />
                  </div>
                  <div className="container_instruction_content">
                    <strong className="container_instruction_title">{t('T40129_$n', {n: 4})}</strong>
                    <p
                      className="container_instruction_desc"
                      style={{whiteSpace: 'pre-wrap'}}
                    >
                      {t('T40070')}
                    </p>
                  </div>
                </SwiperSlide>
              </Swiper>
              <div className="swiper-pagination"></div>
            </div>
          </div>

          <ul className="notice_list">
            <li className="notice_item">{t('T40071')}</li>
            <li className="notice_item">{t('T40072')}</li>
            <li className="notice_item">{t('T40073')}</li>
            <li className="notice_item">{t('T40074')}</li>
          </ul>
        </section>
      </main>

      {/* FIXME */}
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            emitEventLog({
              event: 'mileage_management',
              interaction_type: 'link mileage',
              click_text: t('T40075'),
              click_location: 'body',
            });
            navigate(ROUTE_PATHS.MILEAGE_AGREEMENT.getFullPath(vin), {replace: true});
          }}
        >
          {t('T40075')}
        </button>
      </div>
    </div>
  );
}

export default MileageIntro;
